import { ExperienceRoute, Route } from "data/records";
import { List, Map, Record } from "immutable";
import { apiv3 } from "lib/apiv3";
import { fulfilled, pending } from "lib/promiseMiddlewareHelpers";
import _ from "lodash";

const getActionTypes = (namespace, types) =>
  types.reduce((acc, type) => {
    acc[type] = `${namespace}/${type}`;
    return acc;
  }, {});

const types = getActionTypes("experienceRoutes", ["LOAD_ALL", "ADD_ROUTE", "DELETE", "REORDER"]);

const selectors = {
  find: (state, id) => state.routes.experienceRoutes.get(id),
  routesForExperience: (state, experienceId) => {
    const eRouteIds = state.orderedExperienceRouteIds.get(experienceId);
    const routeIds = eRouteIds.map((id) => state.experienceRoutes.get(id).routeId);
    return routeIds.map((id) => state.routes.get(id));
  },
  experienceRoutesForExperience: (state, experienceId) =>
    state.routes
      .getIn(["orderedExperienceRouteIds", experienceId], Map())
      .map((id) => state.routes.getIn(["experienceRoutes", id], Map())),
};

const ExperienceRoutesState = Record({
  orderedExperienceRouteIds: Map(), // experience id -> List([experienceRouteId])
  experienceRoutes: Map(), // experience route id -> experience route
  routes: Map(), // route id -> route
});

const getStateFromCollectionResponse = (experienceId, state, response) => {
  const newExperienceRoutes = state.experienceRoutes.withMutations((_m) =>
    response.results.reduce((m, record) => m.set(record.id, record), _m)
  );
  const newRoutes = state.routes.withMutations((_m) =>
    response.extras.reduce((m, { id, route }) => (route ? m.set(id, Route.deserialize(route)) : m), _m)
  );
  const newOrderedExperienceRouteIds = state.orderedExperienceRouteIds.set(
    experienceId,
    List(_.sortBy(response.results, (x) => x.position).map((x) => x.id))
  );
  return state
    .set("experienceRoutes", newExperienceRoutes)
    .set("routes", newRoutes)
    .set("orderedExperienceRouteIds", newOrderedExperienceRouteIds);
};

const reducer = (state = ExperienceRoutesState(), action = {}) => {
  switch (action.type) {
    case fulfilled(types.LOAD_ALL): {
      const { experienceId } = action.meta;
      return getStateFromCollectionResponse(experienceId, state, action.payload);
    }
    case fulfilled(types.ADD_ROUTE): {
      const { experienceId } = action.meta;
      const experienceRoute = action.payload.result;
      const newExperienceRoutes = state.experienceRoutes.set(experienceRoute.id, experienceRoute);
      // copy pasta
      const newRoutes = state.routes.withMutations((_m) =>
        action.payload.extras.reduce((m, { id, route }) => (route ? m.set(id, Route.deserialize(route)) : m), _m)
      );
      const newOrderedExperienceRouteIds = state.orderedExperienceRouteIds.set(
        experienceId,
        newExperienceRoutes
          .filter((x) => x.experienceId === experienceId)
          .toList()
          .sortBy((x) => x.position)
          .map((x) => x.id)
      );
      return state
        .set("experienceRoutes", newExperienceRoutes)
        .set("routes", newRoutes)
        .set("orderedExperienceRouteIds", newOrderedExperienceRouteIds);
    }
    case fulfilled(types.DELETE): {
      const { id } = action.meta;
      const { experienceId } = state.experienceRoutes.get(id);
      const orderedIds = state.orderedExperienceRouteIds.get(experienceId);
      const newIds = orderedIds.delete(orderedIds.indexOf(id));
      const newExperienceRoutes = state.experienceRoutes.delete(id);
      return state
        .setIn(["orderedExperienceRouteIds", experienceId], newIds)
        .set("experienceRoutes", newExperienceRoutes);
    }
    case pending(types.REORDER): {
      const { experienceId, experienceRouteIds } = action.meta;
      return state.setIn(["orderedExperienceRouteIds", experienceId], experienceRouteIds);
    }
    case fulfilled(types.REORDER): {
      const { experienceId } = action.meta;
      return getStateFromCollectionResponse(experienceId, state, action.payload);
    }
    default:
      return state;
  }
};

const actions = {
  loadAll: (experienceId, params) => ({
    type: types.LOAD_ALL,
    payload: apiv3.experienceRoutes.list({
      experience_id: experienceId,
      ...params,
    }),
    meta: { experienceId },
  }),
  addRoute: (experienceId, routeId) => ({
    type: types.ADD_ROUTE,
    payload: apiv3.experienceRoutes.create(
      ExperienceRoute({
        experienceId,
        routeId,
      })
    ),
    meta: { experienceId },
  }),
  delete: (id) => ({
    type: types.DELETE,
    payload: apiv3.experienceRoutes.delete(id),
    meta: { id },
  }),
  reorder: (experienceId, experienceRouteIds) => ({
    type: types.REORDER,
    payload: apiv3.experienceRoutes.reorder(experienceId, experienceRouteIds),
    meta: {
      experienceId,
      experienceRouteIds,
    },
  }),
};

export { reducer, actions as experienceRoutesActions, selectors as experienceRoutesSelectors };
